import { useState } from 'react';
import {
	AdminShell,
	MimaText,
	PageLoader,
	MimaButton,
	MimaDropZone,
	MimaInput,
	ReceiptCheckBox as RCB,
	MimaQuillInput,
} from '../../../components';
import { useAdminWebsiteConfigLogic } from './logic';
import styles from '../../../assets/styles/Admin/AdminConfigurations.module.scss';

export const WebsiteConfiguration = () => {
	const { isLoading, formik, sections } = useAdminWebsiteConfigLogic();
	const [openSection, setOpenSection] = useState(sections?.[0]?.id || null);

	const {
		handleChange,
		handleBlur,
		handleSubmit,
		setFieldValue,
		values,
		errors,
		touched,
		isSubmitting,
	} = formik;

	const toggleSizeGuide = value => {
		const currentGuides = values?.website?.sizeGuides || [];
		const updatedGuides = currentGuides.includes(value)
			? currentGuides.filter(item => item !== value)
			: [...currentGuides, value];
		setFieldValue('website.sizeGuides', updatedGuides);
	};

	const renderSectionFields = sectionId => {
		switch (sectionId) {
			case 'menuBar':
				return (
					<div className={styles.checks}>
						<RCB
							title="Collection displayed on menu bar"
							name="website.collectionsMenuStyle"
							onChange={() =>
								setFieldValue(
									'website.collectionsMenuStyle',
									'DISPLAY_ON_MENU_BAR'
								)
							}
							checked={
								values?.website?.collectionsMenuStyle === 'DISPLAY_ON_MENU_BAR'
							}
							value="DISPLAY_ON_MENU_BAR"
						/>
						<RCB
							title="Collection displayed under shop menu"
							name="website.collectionsMenuStyle"
							onChange={() =>
								setFieldValue(
									'website.collectionsMenuStyle',
									'DISPLAY_UNDER_SHOP_MENU'
								)
							}
							checked={
								values?.website?.collectionsMenuStyle ===
								'DISPLAY_UNDER_SHOP_MENU'
							}
							value="DISPLAY_UNDER_SHOP_MENU"
						/>
					</div>
				);
			case 'homeBanner':
				return (
					<div>
						<MimaDropZone
							title="Upload Homepage Banner"
							info="Click to upload banner"
							maxNumOfImages={1}
							files={values?.homeBannerImageUrl || []}
							setFiles={newFiles => {
								setFieldValue('homeBannerImageUrl', newFiles);
							}}
							labelTitle="Upload Image"
							name="homeBannerImageUrl"
						/>
						<MimaInput
							type="text"
							labelTitle="Banner Overlay Text"
							placeholder="Enter Overlay Text"
							name="website.homeBanner.title"
							mb={2}
							value={values?.website?.homeBanner?.title || ''}
							onChange={handleChange}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			case 'aboutUs':
				return (
					<div>
						<MimaDropZone
							title="Upload About Us Picture"
							info="Click to upload picture"
							maxNumOfImages={1}
							files={values?.aboutUsImageUrl || []}
							setFiles={newFiles => {
								setFieldValue('aboutUsImageUrl', newFiles);
							}}
							labelTitle="Upload Image"
							name="aboutUsImageUrl"
						/>
						<MimaQuillInput
							name="website.aboutUs.text"
							labelTitle="About Us Content"
							value={values?.website?.aboutUs?.text}
							error={errors?.website?.aboutUs?.text}
							touched={touched?.website?.aboutUs?.text}
							placeholder="Enter Content"
							mt={2}
							mb={2}
							onChange={content => {
								setFieldValue('website.aboutUs.text', content);
							}}
						/>
					</div>
				);
			case 'localShipping':
				return (
					<div className={styles.gridTwoColumns}>
						<MimaInput
							type="number"
							labelTitle="Local Shipping Fee"
							name="website.shipping.local.shippingFee"
							value={values?.website?.shipping?.local?.shippingFee || 0}
							onChange={handleChange}
							error={errors?.website?.shipping?.local?.shippingFee}
							touched={touched?.website?.shipping?.local?.shippingFee}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
						<MimaInput
							type="number"
							labelTitle="Free Shipping for Orders Above"
							name="website.shipping.local.freeShippingForOrderAbove"
							value={
								values?.website?.shipping?.local?.freeShippingForOrderAbove || 0
							}
							onChange={handleChange}
							error={
								errors?.website?.shipping?.local?.freeShippingForOrderAbove
							}
							touched={
								touched?.website?.shipping?.local?.freeShippingForOrderAbove
							}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			case 'internationalShipping':
				return (
					<div className={styles.gridTwoColumns}>
						<MimaInput
							type="number"
							labelTitle="Shipping Fee (USD)"
							name="website.shipping.international.shippingFee"
							value={values?.website?.shipping?.international?.shippingFee || 0}
							onChange={handleChange}
							error={errors?.website?.shipping?.international?.shippingFee}
							touched={touched?.website?.shipping?.international?.shippingFee}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
						<MimaInput
							type="number"
							labelTitle="Free Shipping for Orders Above (USD)"
							name="website.shipping.international.freeShippingForOrderAbove"
							value={
								values?.website?.shipping?.international
									?.freeShippingForOrderAbove || 0
							}
							onChange={handleChange}
							error={
								errors?.website?.shipping?.international
									?.freeShippingForOrderAbove
							}
							touched={
								touched?.website?.shipping?.international
									?.freeShippingForOrderAbove
							}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			case 'refundPolicy':
				return (
					<div>
						<div className={styles.checks}>
							<RCB
								title="Use default Standard Refund Policy"
								onChange={() =>
									setFieldValue('website.useDefaultRefundPolicy', true)
								}
								checked={!!values?.website?.useDefaultRefundPolicy}
								name="website.useDefaultRefundPolicy"
							/>
							<RCB
								title="State your Custom Policy"
								onChange={() =>
									setFieldValue('website.useDefaultRefundPolicy', false)
								}
								checked={!values?.website?.useDefaultRefundPolicy}
								name="website.useDefaultRefundPolicy"
							/>
						</div>

						{values?.website?.useDefaultRefundPolicy ? (
							<MimaInput
								type="number"
								labelTitle="In How Many Days Is Return Valid"
								name="website.refundPolicyDays"
								value={values?.website?.refundPolicyDays || 0}
								onChange={handleChange}
								error={errors?.website?.refundPolicyDays}
								touched={touched?.website?.refundPolicyDays}
								onBlur={handleBlur}
								variant="fullWidth"
							/>
						) : (
							<MimaQuillInput
								name="website.refundPolicy"
								labelTitle="Refund Policy"
								error={errors?.website?.refundPolicy}
								touched={touched?.website?.refundPolicy}
								placeholder="State policy"
								mt={2}
								mb={2}
								onChange={content => {
									setFieldValue('website.refundPolicy', content);
								}}
								value={values?.website?.refundPolicy}
							/>
						)}
					</div>
				);
			case 'termsOfService':
				return (
					<div>
						<div className={styles.checks}>
							<RCB
								title="Use default Standard Terms of Service"
								onChange={() =>
									setFieldValue('website.useDefaulTermsOfService', true)
								}
								checked={!!values?.website?.useDefaulTermsOfService}
								name="website.useDefaulTermsOfService"
							/>
							<RCB
								title="State your Custom Term"
								onChange={() =>
									setFieldValue('website.useDefaulTermsOfService', false)
								}
								checked={!values?.website?.useDefaulTermsOfService}
								name="website.useDefaulTermsOfService"
							/>
						</div>
						{!values?.website?.useDefaulTermsOfService && (
							<MimaQuillInput
								name="website.termsOfService"
								labelTitle="Terms of Service"
								error={errors?.website?.termsOfService}
								touched={touched?.website?.termsOfService}
								placeholder="Enter Terms of Service"
								mt={2}
								mb={2}
								onChange={content => {
									setFieldValue('website.termsOfService', content);
								}}
								value={values?.website?.termsOfService}
							/>
						)}
					</div>
				);
			case 'faqs': {
				const faqs = values?.website?.faqs || [];
				return (
					<div>
						{faqs.map((faq, index) => (
							<div key={`faq-${index}`}>
								<div
									className={styles.gridTwoColumns}
									style={{ marginBottom: '2rem' }}>
									<MimaInput
										type="text"
										labelTitle={`FAQ Question ${index + 1}`}
										name={`website.faqs.${index}.question`}
										value={faq?.question || ''}
										onChange={handleChange}
										onBlur={handleBlur}
										variant="fullWidth"
									/>
									<MimaInput
										type="text"
										labelTitle={`FAQ Answer ${index + 1}`}
										name={`website.faqs.${index}.text`}
										value={faq?.text || ''}
										onChange={handleChange}
										onBlur={handleBlur}
										variant="fullWidth"
									/>
								</div>
								{index !== 0 && (
									<MimaButton
										type="button"
										title="Remove FAQ"
										variant="colorableSmall"
										onClick={() => {
											const nextFaqs = faqs.filter((_, i) => i !== index);
											setFieldValue('website.faqs', nextFaqs);
										}}
										mb={2}
										mt={2}
										buttonColor="var(--color-error)"
										width={18}
									/>
								)}
							</div>
						))}
						<MimaButton
							type="button"
							title="Add Another FAQ"
							variant="outlined"
							onClick={() => {
								setFieldValue('website.faqs', [
									...faqs,
									{ question: '', text: '' },
								]);
							}}
							mt={2}
						/>
					</div>
				);
			}
			case 'sizeGuide':
				return (
					<div className={styles.checks}>
						<RCB
							title="Male"
							onChange={() => toggleSizeGuide('MALE')}
							checked={values?.website?.sizeGuides?.includes('MALE')}
							value="MALE"
						/>
						<RCB
							title="Female"
							onChange={() => toggleSizeGuide('FEMALE')}
							checked={values?.website?.sizeGuides?.includes('FEMALE')}
							value="FEMALE"
						/>
						<RCB
							title="Children"
							onChange={() => toggleSizeGuide('CHILD')}
							checked={values?.website?.sizeGuides?.includes('CHILD')}
							value="CHILD"
						/>
					</div>
				);
			case 'maintenanceMode':
				return (
					<div className={styles.checks}>
						<RCB
							title="Yes"
							onChange={() => setFieldValue('website.maintenanceMode', true)}
							checked={!!values?.website?.maintenanceMode}
							name="website.maintenanceMode"
						/>
						<RCB
							title="No"
							onChange={() => setFieldValue('website.maintenanceMode', false)}
							checked={!values?.website?.maintenanceMode}
							name="website.maintenanceMode"
						/>
					</div>
				);
			case 'liveChat':
				return (
					<div className={styles.gridTwoColumns}>
						<MimaInput
							type="text"
							labelTitle="Property Id"
							name="tawk.propertyId"
							value={values?.tawk?.propertyId || ''}
							onChange={handleChange}
							error={errors?.tawk?.propertyId}
							touched={touched?.tawk?.propertyId}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
						<MimaInput
							type="text"
							labelTitle="Widget Id"
							name="tawk.widgetId"
							value={values?.tawk?.widgetId || ''}
							onChange={handleChange}
							error={errors?.tawk?.widgetId}
							touched={touched?.tawk?.widgetId}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			case 'googleTag':
				return (
					<div className={styles.gridTwoColumns}>
						<MimaInput
							type="text"
							labelTitle="Google Tag Id"
							name="googleTag.id"
							value={values?.googleTag?.id || ''}
							onChange={handleChange}
							error={errors?.googleTag?.id}
							touched={touched?.googleTag?.id}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
						<MimaInput
							type="text"
							labelTitle="Google Tag Url"
							name="googleTag.url"
							value={values?.googleTag?.url || ''}
							onChange={handleChange}
							error={errors?.googleTag?.url}
							touched={touched?.googleTag?.url}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			case 'pinterestTag':
				return (
					<div className={styles.gridTwoColumns}>
						<MimaInput
							type="text"
							labelTitle="Pinterest Tag Id"
							name="pinterest.id"
							value={values?.pinterest?.id || ''}
							onChange={handleChange}
							error={errors?.pinterest?.id}
							touched={touched?.pinterest?.id}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
						<MimaInput
							type="text"
							labelTitle="Pinterest email"
							name="pinterest.email"
							value={values?.pinterest?.email || ''}
							onChange={handleChange}
							error={errors?.pinterest?.email}
							touched={touched?.pinterest?.email}
							onBlur={handleBlur}
							variant="fullWidth"
						/>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<AdminShell pageTitle={'Website Configuration'}>
			{isLoading ? (
				<PageLoader />
			) : (
				<form onSubmit={handleSubmit} className={styles.formContainer}>
					<div className={styles.accordionWrapper}>
						{(sections || []).map(section => {
							const isOpen = openSection === section.id;
							return (
								<div key={section.id} className={styles.accordionItem}>
									<button
										type="button"
										onClick={() => setOpenSection(isOpen ? null : section.id)}
										className={`${styles.accordionHeader} ${isOpen ? styles.isOpen : ''}`}>
										<MimaText variant="bodyBold">{section.title}</MimaText>
										<span className={styles.accordionIcon}>›</span>
									</button>
									{isOpen && (
										<div className={styles.accordionContent}>
											{renderSectionFields(section.id)}
										</div>
									)}
								</div>
							);
						})}
					</div>

					<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<MimaButton
							title="Save Changes"
							variant="colorableSmall"
							type="submit"
							loading={isSubmitting}
							className={styles.submitButton}
							buttonColor="var(--color-fash-black)"
						/>
					</div>
				</form>
			)}
		</AdminShell>
	);
};

export default WebsiteConfiguration;
